import React from 'react';
import { isEmpty, map } from 'lodash';
import classnames from 'classnames';

// local dependencies
import { getLabel } from 'utils/getLabel';
import { button as Button } from 'modules/button';
import { SIZES } from 'constants/common';
import { withNetworkState } from 'ui/hoc/withNetworkState';
import { Icon } from 'modules/icon-bank';
import { inputField as InputField } from 'ui/views/generic/inputField';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { memberInfoCommunicationSettingsWellConnect as MemberInfoCommunicationSettingsWellConnect } from 'ui/views/widgets/memberInfoCommunicationSettingsWellConnect';
 *
 * // render
 * <MemberInfoCommunicationSettingsWellConnect propName='propValue'/>
 */
const _memberInfoCommunicationSettingsWellConnect = ( props ) => {
    const phoneSection = props.phoneNumberSection;
    const phoneField = phoneSection.field;

    const optOutSection = props.optOutSection;
    const optOutPhoneField = optOutSection.phoneField;

    const values = props.values;

    return (
        <div className='ui-member-info-communication-settings-well-connect'>

            {/* static description */}
            <div
                className='ui-member-info-communication-settings-well-connect__static-description'
                dangerouslySetInnerHTML={ { __html: getLabel( props.staticDescription.description ) } }
                onClick={ ( event ) => {
                    const actionName = event.target.parentElement.dataset.action;

                    if ( 'hereAction' === actionName ) {
                        props.onActionClick( {
                            name: actionName,
                            action: props.staticDescription.hereAction
                        } );
                    }
                } }
            >
            </div>

            {/* dynamic description */}
            <div className='ui-member-info-communication-settings-well-connect__dynamic-description'>
                {
                    isEmpty( optOutSection ) ? (
                        getLabel( props.dynamicDescription.description, values )
                    ) : (
                        getLabel( optOutSection.dynamicDescription )
                    )
                }
            </div>

            {/* phone number section (member is opted in and opt out section is not visible) */}
            {
                ( isEmpty( phoneSection ) || ! isEmpty( optOutSection ) ) ? null : (
                    <div className='ui-member-info-communication-settings-well-connect__phone-section'>
                        
                        {/* phone number field (readonly) */}
                        <InputField
                            className='ui-member-info-communication-settings-well-connect__phone-section__field'
                            name={ phoneField.name }
                            label={ phoneField.title }
                            value={ values[ phoneField.name ] }
                            mode={ phoneField.config.mode }
                        />

                        {/* edit action */}
                        <div
                            className='ui-member-info-communication-settings-well-connect__phone-section__action'
                            onClick={ () => props.onActionClick( { name: 'editPhone', action: phoneSection.editAction } ) }
                        >
                            <div className='ui-member-info-communication-settings-well-connect__phone-section__action__icon'>
                                <Icon name='edit' />
                            </div>
                            <div className='ui-member-info-communication-settings-well-connect__phone-section__action__title'>
                                { getLabel( phoneSection.editAction.title ) }
                            </div>
                        </div>
                    </div>
                )
            }

            {/* actions (if opt out section is not visible) */}
            {
                isEmpty( optOutSection ) ? (
                    <div className='ui-member-info-communication-settings-well-connect__actions'>
                        {
                            map( props.actions, ( action ) => {
                                const actionName = action.name;
                                const actionClassName = classnames(
                                    'ui-member-info-communication-settings-well-connect__actions__action',
                                    `ui-member-info-communication-settings-well-connect__actions__${ actionName }`
                                );

                                return (
                                    <Button
                                        className={ actionClassName }
                                        key={ actionName }
                                        id={ actionName }
                                        title={ action.title }
                                        size={ SIZES.LARGE }
                                        outline={ true }
                                        onClick={ () => props.onActionClick( { name: actionName, action } ) }
                                    />
                                );
                            } )
                        }
                    </div>
                ) : null
            }

            {/* opt out section */}
            {
                isEmpty( optOutSection ) ? null : (
                    <div className='ui-member-info-communication-settings-well-connect__opt-out-section'>

                        {/* phone number field (disabled) */}
                        <InputField
                            className='ui-member-info-communication-settings-well-connect__opt-out-section__field'
                            showLabel={ false }
                            value={ values.phoneNumber }
                            disabled={ optOutPhoneField.config.disabled }
                        />

                        {/* actions */}
                        <div className='ui-member-info-communication-settings-well-connect__opt-out-section__actions'>
                            {
                                map( optOutSection.actions, ( action ) => {
                                    const actionName = `${action.name}OptOut`;

                                    return (
                                        <Button
                                            className='ui-member-info-communication-settings-well-connect__opt-out-section__actions__action'
                                            key={ actionName }
                                            id={ actionName }
                                            title={ action.title }
                                            size={ SIZES.LARGE }
                                            outline={ 'cancel' === action.name ? true : false }
                                            onClick={ () => props.onActionClick( { name: actionName, action } ) }
                                        />
                                    );
                                } )
                            }
                        </div>
                    </div>
                )
            }
        </div>
    );
};

// set display name
_memberInfoCommunicationSettingsWellConnect.displayName = 'memberInfoCommunicationSettingsWellConnect';

// set default props
_memberInfoCommunicationSettingsWellConnect.defaultProps = {};

// prop types
_memberInfoCommunicationSettingsWellConnect.propTypes = {};


const memberInfoCommunicationSettingsWellConnectPreloader = () => {
    return (
        <div className='ui-member-info-communication-settings-well-connect-preloader'>
            <div className='ui-member-info-communication-settings-well-connect-preloader__static'>
                {
                    map( [ 1, 2 ], ( line ) => {
                        return (
                            <div
                                key={ line }
                                className='ui-member-info-communication-settings-well-connect-preloader__static__line'>
                            </div>
                        );
                    } )
                }
            </div>
            <div className='ui-member-info-communication-settings-well-connect-preloader__dynamic'></div>
            <div className='ui-member-info-communication-settings-well-connect-preloader__actions'>
                {
                    map( [ 1, 2 ], ( action ) => {
                        return (
                            <div
                                key={ action }
                                className='ui-member-info-communication-settings-well-connect-preloader__actions__action'>
                            </div>
                        );
                    } )
                }
            </div>
        </div>
    );
};

// set display name
memberInfoCommunicationSettingsWellConnectPreloader.displayName = 'memberInfoCommunicationSettingsWellConnectPreloader';

// set default props
memberInfoCommunicationSettingsWellConnectPreloader.defaultProps = {};

// prop types
memberInfoCommunicationSettingsWellConnectPreloader.propTypes = {};


export const memberInfoCommunicationSettingsWellConnect = withNetworkState( memberInfoCommunicationSettingsWellConnectPreloader, _memberInfoCommunicationSettingsWellConnect );
